import { useEffect, useState } from "react";
import { usePermissions } from '@/services/permissionHelper';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminService } from '@/services/adminService';
import { toast } from "sonner";
import ReviewsManagementHeader from "@/components/admin/ReviewsManagementHeader";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import ReviewManagementTabs from "@/components/admin/ReviewManagementTabs";
import { CacheDebugPanel } from "@/components/admin/CacheDebugPanel";

const ReportedReviewsPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("table");
  const { data: bundle, isLoading } = useQuery({
    queryKey: ['admin-reported-review-bundle'],
    queryFn: () => adminService.getReportedReviewsBundle(),
    enabled: isAdmin,
  });

  const reviewsReports = bundle?.reports ?? [];
  const reportedReviews = bundle?.reviews ?? [];

  const refreshData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-reported-review-bundle'] }),
      queryClient.invalidateQueries({ queryKey: ['adminReviews'] }),
    ]);
  };

  const markReportResolved = async (reportId: string) => {
    if (!isAdmin) return;

    setIsProcessing(true);
    try {
      const result = await adminService.resolveReviewReport(reportId, "Review moderated by admin");
      const success = result.success;
      if (success) {
        toast.success("Report marked as resolved");
        await refreshData();
      } else {
        toast.error("Failed to mark report as resolved");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while resolving the report");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleVisibility = async (reviewId: string, newVisibility: boolean) => {
    if (!isAdmin) return;

    setIsProcessing(true);
    try {
      const reviewToUpdate = reportedReviews.find(review => review.id === reviewId);
      const successRes = await adminService.updateReviewVisibility(reviewId, newVisibility, queryClient, reviewToUpdate?.providerId);
      const success = successRes.success;

      if (!success) {
        throw new Error("Failed to update review visibility");
      }

      await refreshData();

      toast.success(`Review visibility updated to ${newVisibility ? "Public" : "Hidden"}`);
    } catch (error) {
      console.error("Error updating review visibility:", error);
      toast.error("Failed to update review visibility");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!isAdmin) return;

    setIsProcessing(true);
    try {
      const reviewToDelete = reportedReviews.find(review => review.id === reviewId);
      const successRes = await adminService.deleteReview(reviewId, queryClient, reviewToDelete?.providerId);
      const success = successRes.success;
      if (success) {
        await refreshData();
        
        toast.success("Review deleted successfully");
      } else {
        toast.error("Failed to delete review");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while deleting the review");
    } finally {
      setIsProcessing(false);
    }
  };

  const purgeOrphanedReports = async () => {
    if (!isAdmin) return;

    setIsPurging(true);
    try {
      const result = await adminService.purgeOrphanedReviewReports();
      const orphanedCount = result.success ? result.data?.deletedCount ?? 0 : 0;

      if (orphanedCount === 0) {
        toast.info("No orphaned review reports found to purge");
        return;
      }

      toast.success(`Successfully purged ${orphanedCount} orphaned review reports`);
      
      await refreshData();
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while purging orphaned reports");
    } finally {
      setIsPurging(false);
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout title="Access Denied">
        <Card>
          <CardContent className="p-6">
            <p className="text-center">You do not have permission to access this page.</p>
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  if (isLoading) {
    return <AdminLayout title="Reported Reviews" loading={true} />;
  }

  return (
    <AdminLayout title="Reported Reviews">
      <div className="space-y-6">
        <ReviewsManagementHeader 
          isPurging={isPurging}
          onPurge={purgeOrphanedReports}
        />
        <ReviewManagementTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          reviewsReports={reviewsReports}
          reportedReviews={reportedReviews}
          onResolveReport={markReportResolved}
          onVisibilityToggle={handleToggleVisibility}
          onDeleteReview={handleDeleteReview}
          isProcessing={isProcessing}
        />
        
        {/* Cache Debug Panel */}
        <CacheDebugPanel />
      </div>
    </AdminLayout>
  );
};

export default ReportedReviewsPage;
